import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore } from 'firebase/firestore';
import { getWorkOrdersByCompany, getPMWorkOrdersByCompany, getUsersByCompany, getAssetsByCompany } from '@cmms/api';
import { WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';

@customElement('wo-mini-dashboard')
export class WOMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private workOrders: Array<any> = [];
  @state() private users: Record<string, string> = {};
  @state() private assets: Record<string, string> = {};

  @state() private thirtyDayCreatedSeries: number[] = [];
  @state() private thirtyDayCompletedSeries: number[] = [];
  @state() private thirtyDayLabels: string[] = [];

  // SLA-style target response window for reactive WOs (hours)
  private readonly targetResponseHours = 8;

  static styles = [
    styles,
    css`
      .mini-dashboard {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        background: var(--sl-color-neutral-50);
        padding: 1rem;
      }
      .mini-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;
      }
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 1.5rem;
      }
      @media (max-width: 900px) {
        .kpi-grid { grid-template-columns: 1fr; }
      }
      .kpi-card {
        background: white;
        border-radius: 1rem;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.06);
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .kpi-header { color: var(--sl-color-neutral-600); font-size: 0.85rem; }
      .kpi-body { display:flex; align-items:center; gap:0.75rem; }
      .kpi-value { font-size: 1.5rem; font-weight: 600; color: var(--sl-color-neutral-900); }
      .kpi-value-small { font-size: 0.95rem; color: var(--sl-color-neutral-900); }
      .kpi-suffix { font-size: 0.9rem; color: var(--sl-color-neutral-600); margin-left: 0.25rem; }
      .donut-chart {
        width: 140px;
        height: 140px;
        border-radius: 50%;
        position: relative;
        margin: 0 auto;
        box-shadow: inset 0 0 0 18px #fff;
      }
      .chart-legend {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .legend-item { display:flex; align-items:center; gap:0.5rem; }
      .legend-color { width:12px; height:12px; border-radius:50%; }
      .widget .content { display:flex; gap:1.5rem; align-items:center; }
      .charts-grid { display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.5rem; }
      @media (max-width: 900px) { .charts-grid { grid-template-columns: 1fr; } }
      .tables-grid { display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.5rem; }
      @media (max-width: 900px) { .tables-grid { grid-template-columns: 1fr; } }
      .line-chart { background:white; border-radius:1rem; padding:0.5rem; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.06); }
      .line-legend { display:flex; align-items:center; font-size: 0.85rem; color: var(--sl-color-neutral-700); padding: 0.25rem 0.25rem 0.5rem; }
      .line-legend .dot { width:10px; height:10px; border-radius:50%; display:inline-block; margin-right:0.5rem; }
      .data-table { width: 100%; border-collapse: collapse; }
      .data-table th, .data-table td { padding: 0.75rem; text-align: left; border-bottom: 1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight: 600; font-size: 0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size: 0.875rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const [woResp, pmResp, usersResp, assetsResp] = await Promise.all([
        getWorkOrdersByCompany(this.firestore, this.companyId),
        getPMWorkOrdersByCompany(this.firestore, this.companyId),
        getUsersByCompany(this.firestore, this.companyId),
        getAssetsByCompany(this.firestore, this.companyId)
      ]);

      const rawWOs = this.asArray<any>(woResp);
      const rawPM = this.asArray<any>(pmResp);
      const rawUsers = this.asArray<any>(usersResp);
      const rawAssets = this.asArray<any>(assetsResp);
      
      // Combine regular work orders and PM work orders
      const allWorkOrders = [...rawWOs, ...rawPM];

      // Users map
      const usersMap: Record<string, string> = {};
      for (const u of rawUsers) {
        const model = (u as any).model || {};
        const id = (u as any).id;
        const name = model.full_name || model.name || (u as any).name || model.email || 'User';
        if (id) usersMap[id] = name;
      }
      this.users = usersMap;

      // Assets map
      const assetsMap: Record<string, string> = {};
      for (const a of rawAssets) {
        const model = (a as any).model || {};
        const id = (a as any).id;
        const name = model.name || (a as any).name || 'Asset';
        if (id) assetsMap[id] = name;
      }
      this.assets = assetsMap;

      // Normalize WOs using the same method as work orders page
      this.workOrders = (allWorkOrders || []).map((wo: any) => {
        // Extract data from various possible structures (direct, model property, etc.)
        const model = wo.model || {};
        const data = { ...wo, ...model }; // Merge direct properties with model properties
        
        // Handle created_at normalization
        let created_at: number | null = null;
        const createdRaw = data.created_at || wo.created_at || model.created_at;
        if (typeof createdRaw === 'number') {
          created_at = createdRaw < 1e12 ? createdRaw * 1000 : createdRaw;
        } else if (createdRaw?.toMillis) {
          created_at = createdRaw.toMillis();
        } else if (createdRaw?.seconds) {
          created_at = createdRaw.seconds * 1000;
        }

        // Handle completed_at normalization
        let completed_at: number | null = null;
        const completedRaw = data.completed_at || wo.completed_at || model.completed_at;
        if (typeof completedRaw === 'number') {
          completed_at = completedRaw < 1e12 ? completedRaw * 1000 : completedRaw;
        } else if (completedRaw?.toMillis) {
          completed_at = completedRaw.toMillis();
        } else if (completedRaw?.seconds) {
          completed_at = completedRaw.seconds * 1000;
        }

        // Handle due_date normalization (for PM work orders)
        let due_date: number | null = null;
        const dueRaw = data.due_date || wo.due_date || model.due_date || 
                      data.pm_due_at || wo.pm_due_at || model.pm_due_at;
        if (typeof dueRaw === 'number') {
          due_date = dueRaw < 1e12 ? dueRaw * 1000 : dueRaw;
        } else if (dueRaw?.toMillis) {
          due_date = dueRaw.toMillis();
        } else if (dueRaw?.seconds) {
          due_date = dueRaw.seconds * 1000;
        }

        // Handle status normalization
        let status_cat = WorkOrderStatusCat.OPEN;
        const statusVal = data.status_cat || wo.status_cat || model.status_cat || data.status || wo.status || model.status;
        if (typeof statusVal === 'number') {
          status_cat = statusVal;
        } else if (statusVal) {
          const statusStr = String(statusVal).toLowerCase();
          if (statusStr.includes('progress')) status_cat = WorkOrderStatusCat.IN_PROGRESS;
          else if (statusStr.includes('complete')) status_cat = WorkOrderStatusCat.COMPLETED;
          else status_cat = WorkOrderStatusCat.OPEN;
        }

        // Handle priority normalization
        let priority_cat = null;
        const priorityVal = data.priority_cat || wo.priority_cat || model.priority_cat || data.priority || wo.priority || model.priority;
        if (typeof priorityVal === 'number') {
          priority_cat = priorityVal;
        } else if (priorityVal) {
          const priorityStr = String(priorityVal).toLowerCase();
          if (priorityStr.includes('high')) priority_cat = WorkOrderPriorityCat.HIGH;
          else if (priorityStr.includes('medium')) priority_cat = WorkOrderPriorityCat.MEDIUM;
          else if (priorityStr.includes('low')) priority_cat = WorkOrderPriorityCat.LOW;
        }

        // Determine if this is a PM work order
        const isPM = !!(data.is_pm || wo.is_pm || model.is_pm || 
                       data.pm_due_at || wo.pm_due_at || model.pm_due_at ||
                       wo.id?.includes('pm') || model.pm_schedule_id);

        return {
          id: wo.id,
          company_id: data.company_id || wo.company_id || model.company_id,
          asset_id: data.asset_id || wo.asset_id || model.asset_id || '',
          description: data.description || wo.description || model.description || '',
          assigned_to: data.assigned_to || wo.assigned_to || model.assigned_to || null,
          priority_cat,
          status_cat,
          created_at,
          completed_at,
          due_date,
          type: isPM ? 'preventive' : 'corrective'
        } as any;
      });

      this.prepareThirtyDaySeries();
    } catch (err) {
      console.error('Failed to load work orders:', err);
      this.workOrders = [];
    }
  }

  private asArray<T = any>(resp: unknown): T[] {
    if (Array.isArray(resp)) return resp as T[];
    return (resp as any).data as T[] || [];
  }

  private formatDate(ts?: number | null) {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString();
  }

  private getStatusLabel(status_cat: number): string {
    switch (status_cat) {
      case WorkOrderStatusCat.IN_PROGRESS: return 'In Progress';
      case WorkOrderStatusCat.COMPLETED: return 'Completed';
      default: return 'Open';
    }
  }

  private getStatusVariant(status_cat: number): 'primary' | 'warning' | 'success' | 'neutral' {
    switch (status_cat) {
      case WorkOrderStatusCat.IN_PROGRESS: return 'warning';
      case WorkOrderStatusCat.COMPLETED: return 'success';
      case WorkOrderStatusCat.OPEN: return 'primary';
      default: return 'neutral';
    }
  }

  private getUserName(userId: string | null | undefined): string {
    if (!userId) {
      return 'Unassigned';
    }
    
    const userName = this.users[userId];
    if (userName) {
      return userName;
    }
    
    // If user ID exists but not found in users map, show user ID for debugging
    return `User (${userId})`;
  }

  // Utilities
  private groupBy<T extends Record<string, any>>(arr: T[], key: keyof T): Record<string, T[]> {
    return (arr || []).reduce((acc: Record<string, T[]>, item: T) => {
      const k = (item[key] ?? '') + '';
      if (!acc[k]) acc[k] = [];
      acc[k].push(item);
      return acc;
    }, {});
  }

  private calculateAverageCompletion(workOrders: any[]): number {
    const completed = (workOrders || []).filter(w => !!w.completed_at && !!w.created_at);
    if (completed.length === 0) return 0;
    const avgMs = completed.reduce((sum, w) => sum + Math.max(0, (w.completed_at as number) - (w.created_at as number)), 0) / completed.length;
    return +(avgMs / (1000 * 60 * 60)).toFixed(1);
  }

  private calculateOnTimeRate(workOrders: any[]): number {
    const msPerHour = 3600000;
    const completed = (workOrders || []).filter(w => !!w.completed_at && !!w.created_at);
    const evaluated = completed
      .map(w => {
        // For preventive (PM), use provided due_date; for reactive, derive SLA from created_at
        const isPm = w.type === 'preventive';
        const derivedDue = isPm
          ? (w.due_date as number | null) ?? null
          : ((w.created_at as number | null) !== null ? (w.created_at as number) + this.targetResponseHours * msPerHour : null);
        return { completed_at: w.completed_at as number, due: derivedDue };
      })
      .filter(x => x.due !== null);
    if (evaluated.length === 0) return 0;
    const onTime = evaluated.filter(x => x.completed_at <= (x.due as number)).length;
    return Math.round((onTime / Math.max(1, evaluated.length)) * 100);
  }

  private calculateMetrics(workOrders: any[]) {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const inMonth = (workOrders || []).filter(w => (w.created_at ?? 0) >= startOfMonth);

    const totalMonth = inMonth.length;
    const openCount = (workOrders || []).filter(w => w.status_cat === WorkOrderStatusCat.OPEN).length;
    const ipCount = (workOrders || []).filter(w => w.status_cat === WorkOrderStatusCat.IN_PROGRESS).length;
    const compCount = (workOrders || []).filter(w => w.status_cat === WorkOrderStatusCat.COMPLETED).length;
    const avgCompletionHrs = this.calculateAverageCompletion((workOrders || []).filter(w => !!w.completed_at));
    const onTimePct = this.calculateOnTimeRate((workOrders || []));
    const planned = (workOrders || []).filter(w => w.type === 'preventive').length;
    const reactive = (workOrders || []).filter(w => w.type !== 'preventive').length;
    const totalType = Math.max(1, planned + reactive);
    const reactivePct = Math.round((reactive / totalType) * 100);
    const plannedPct = 100 - reactivePct;
    const overdue = (workOrders || []).filter(w => !!w.due_date && !w.completed_at && (w.due_date as number) < Date.now()).length;

    return { totalMonth, openCount, ipCount, compCount, avgCompletionHrs, onTimePct, reactivePct, plannedPct, overdue };
  }

  private prepareThirtyDaySeries() {
    const days = 30;
    const today = new Date();
    const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const labels: string[] = [];
    const createdCounts = new Array(days).fill(0);
    const completedCounts = new Array(days).fill(0);

    const dayStartTimes: number[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const ts = midnight(d);
      dayStartTimes.push(ts);
      labels.push(`${d.getMonth() + 1}/${d.getDate()}`);
    }

    const nextStart = (idx: number) => (idx + 1 < dayStartTimes.length ? dayStartTimes[idx + 1] : midnight(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)));

    (this.workOrders || []).forEach(w => {
      const c = w.created_at ?? null;
      const f = w.completed_at ?? null;
      for (let i = 0; i < dayStartTimes.length; i++) {
        const start = dayStartTimes[i];
        const end = nextStart(i);
        if (c !== null && c >= start && c < end) createdCounts[i]++;
        if (f !== null && f >= start && f < end) completedCounts[i]++;
      }
    });

    this.thirtyDayLabels = labels;
    this.thirtyDayCreatedSeries = createdCounts;
    this.thirtyDayCompletedSeries = completedCounts;
  }

  // === Diagnostics ===
  private runDiagnostics(rawNonPm: any[], rawPm: any[]) {
    try {
      const toMs = (raw: any): number | null => {
        if (raw == null) return null;
        if (typeof raw === 'number') return raw < 1e12 ? raw * 1000 : raw;
        if ((raw as any)?.toMillis) return (raw as any).toMillis();
        if ((raw as any)?.seconds) return (raw as any).seconds * 1000;
        return null;
      };

      const rawCombined = [...(rawNonPm || []), ...(rawPm || [])];
      const rawMapped = rawCombined.map((item: any) => {
        const m = item.model || {};
        return {
          id: item.id,
          status_cat: (m as any).status_cat ?? (item as any).status_cat ?? null,
          created_at: (m as any).created_at ?? (item as any).created_at ?? null,
          completed_at: (m as any).completed_at ?? (item as any).completed_at ?? null,
          due_date: (m as any).due_date ?? (item as any).due_date ?? null,
          pm_due_at: (m as any).pm_due_at ?? (item as any).pm_due_at ?? null,
          is_pm: !!((m as any).is_pm ?? (item as any).is_pm ?? false)
        };
      });

      // Log each WO with requested fields (raw)
      console.groupCollapsed('[Diagnostics] Raw WO fields');
      rawMapped.forEach((r) => {
        console.log({
          id: r.id,
          status_cat: r.status_cat,
          created_at: toMs(r.created_at),
          completed_at: toMs(r.completed_at),
          due_date: toMs(r.due_date),
          pm_due_at: toMs(r.pm_due_at),
          is_pm: r.is_pm
        });
      });
      console.groupEnd();

      // Also log normalized (what the dashboard uses)
      console.groupCollapsed('[Diagnostics] Normalized WO sample (first 10)');
      (this.workOrders || []).slice(0, 10).forEach(w => {
        console.log({ id: w.id, status_cat: w.status_cat, created_at: w.created_at, completed_at: w.completed_at, due_date: w.due_date, type: w.type });
      });
      console.groupEnd();

      const completedEnumValue = WorkOrderStatusCat.COMPLETED;
      const uniqueStatusValues = Array.from(new Set(rawMapped.map(r => r.status_cat))).sort();
      const completedByStatus = rawMapped.filter(r => Number(r.status_cat) === Number(completedEnumValue));
      const withCompletedAt = rawMapped.filter(r => toMs(r.completed_at) !== null);

      const durationsHrs = rawMapped.map(r => {
        const c = toMs(r.created_at); const d = toMs(r.completed_at);
        if (c !== null && d !== null) return Math.max(0, d - c) / (1000 * 60 * 60);
        return null;
      }).filter(v => v !== null) as number[];
      const avgManual = durationsHrs.length ? +(durationsHrs.reduce((a, b) => a + b, 0) / durationsHrs.length).toFixed(1) : 0;

      let duePresent = 0, onTime = 0, late = 0;
      completedByStatus.forEach(r => {
        const completedMs = toMs(r.completed_at);
        const dueMs = toMs(r.due_date) ?? toMs(r.pm_due_at);
        if (completedMs !== null && dueMs !== null) {
          duePresent++;
          if (completedMs <= dueMs) onTime++; else late++;
        }
      });

      // Normalized checks as well
      const normCompleted = (this.workOrders || []).filter(w => w.status_cat === completedEnumValue);
      const normWithCompletedAt = normCompleted.filter(w => !!w.completed_at);
      const normDurations = normWithCompletedAt.map(w => Math.max(0, (w.completed_at as number) - (w.created_at as number)) / 3.6e6);
      const normAvg = normDurations.length ? +(normDurations.reduce((a, b) => a + b, 0) / normDurations.length).toFixed(1) : 0;
      const normOnTimeBase = normCompleted.filter(w => !!(w.due_date));
      const normOnTime = normOnTimeBase.filter(w => (w.completed_at as number) <= (w.due_date as number)).length;
      const normLate = normOnTimeBase.length - normOnTime;

      console.group('[Diagnostics] Work Orders Dashboard Report');
      console.log('✅ Total WOs fetched:', rawMapped.length);
      console.log('✅ Number completed (by status):', completedByStatus.length);
      console.log('✅ Number with valid completed_at:', withCompletedAt.length);
      console.log('✅ Average completion time (hrs, manual):', avgManual);
      console.log('✅ Number with valid due_date/pm_due_at:', duePresent);
      console.log('✅ Count on-time vs late:', { onTime, late });
      console.log('✅ WorkOrderStatusCat.COMPLETED value:', completedEnumValue);
      console.log('ℹ️ Unique status_cat values in data:', uniqueStatusValues);
      console.log('ℹ️ Normalized metrics cross-check:', {
        normalizedCompleted: normCompleted.length,
        normalizedWithCompletedAt: normWithCompletedAt.length,
        normalizedAvgHrs: normAvg,
        normalizedOnTimeVsLate: { onTime: normOnTime, late: normLate }
      });
      // Additional required summary per request using derived due for reactive
      const msPerHour = 3600000;
      const reactiveCount = (this.workOrders || []).filter(w => w.type !== 'preventive').length;
      const preventiveCount = (this.workOrders || []).filter(w => w.type === 'preventive').length;
      const completedNorm = (this.workOrders || []).filter(w => !!w.completed_at && !!w.created_at);
      const withValidTimes = completedNorm.length;
      const avgCompletionHrs = this.calculateAverageCompletion(completedNorm);
      let onTimeCount = 0, lateCount = 0;
      completedNorm.forEach(w => {
        const isPm = w.type === 'preventive';
        const due = isPm ? (w.due_date as number | null) : ((w.created_at as number) + this.targetResponseHours * msPerHour);
        if (due != null) {
          if ((w.completed_at as number) <= due) onTimeCount++; else lateCount++;
        }
      });
      console.log('[Diagnostics] Summary:', {
        totalCompleted: completedNorm.length,
        withValidTimes,
        avgCompletionHrs,
        onTimeCount,
        lateCount,
        reactiveCount,
        preventiveCount
      });
      console.groupEnd();

      console.log('Diagnostics Report ready');
    } catch (e) {
      console.warn('Diagnostics failed:', e);
    }
  }

  // === Focused Diagnostics for Top 5 widgets ===
  private runTop5Diagnostics() {
    try {
      const msPerHour = 3600000;
      const completed = (this.workOrders || []).filter(w => w.status_cat === WorkOrderStatusCat.COMPLETED);

      // 1) Technician assignment data over completed WOs
      console.group('[Diagnostics: Technician Details]');
      const techData = completed.map(w => {
        const techName = this.users[w.assigned_to] || 'Unassigned';
        const durationHrs = (w.completed_at && w.created_at) ? Math.max(0, (w.completed_at as number) - (w.created_at as number)) / msPerHour : null;
        const rec = {
          id: w.id,
          assigned_to: w.assigned_to || null,
          resolvedTechName: techName,
          created_at: w.created_at || null,
          completed_at: w.completed_at || null,
          durationHrs
        };
        console.log(rec);
        return rec;
      });
      console.groupEnd();

      const totalCompleted = completed.length;
      const assignedCount = completed.filter(w => !!w.assigned_to && !!this.users[w.assigned_to]).length;
      const unassignedCount = totalCompleted - assignedCount;
      const uniqueTechs = Array.from(new Set(completed.map(w => this.users[w.assigned_to] || 'Unassigned')));

      console.group('[Diagnostics: Technician Summary]');
      console.log('✅ Total completed:', totalCompleted);
      console.log('✅ Assigned count:', assignedCount);
      console.log('✅ Unassigned count:', unassignedCount);
      console.log('✅ Unique technicians:', uniqueTechs);
      console.log('✅ Sample tech data (first 5):', techData.slice(0,5));
      console.groupEnd();

      // 2) Asset references across all WOs
      console.group('[Diagnostics: Asset Details]');
      const assetData = (this.workOrders || []).map(w => {
        const assetName = this.assets[w.asset_id] || 'Unknown Asset';
        const rec = { id: w.id, asset_id: w.asset_id || null, resolvedAssetName: assetName };
        console.log(rec);
        return rec;
      });
      console.groupEnd();

      const byAsset: Record<string, number> = {};
      (this.workOrders || []).forEach(w => {
        const key = (w.asset_id || '');
        byAsset[key] = (byAsset[key] || 0) + 1;
      });
      const assetCounts = Object.entries(byAsset);
      const uniqueAssets = assetCounts.filter(([k]) => !!k).length;
      const topAssets = assetCounts
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([assetId, count]) => ({ name: this.assets[assetId] || (assetId || 'Unknown Asset'), count }));

      console.group('[Diagnostics: Asset Summary]');
      console.log('✅ Total unique assets:', assetCounts.length);
      console.log('✅ Top 5 assets by WO count:', topAssets);
      console.log('✅ Sample asset data (first 5):', assetData.slice(0,5));
      console.groupEnd();

      // 3) Date window: completed in current month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startMs = startOfMonth.getTime();
      const monthlyCompletedCount = completed.filter(w => !!w.completed_at && (w.completed_at as number) >= startMs).length;

      console.group('[Diagnostics: Monthly Window]');
      console.log('✅ Completed this month:', monthlyCompletedCount);
      console.groupEnd();

      console.log('Top 5 Diagnostics Expanded Report ready');
    } catch (e) {
      console.warn('Top 5 diagnostics failed:', e);
    }
  }

  render() {
    const metrics = this.calculateMetrics(this.workOrders);
    const open = metrics.openCount;
    const inProgress = metrics.ipCount;
    const completed = metrics.compCount;
    const total = Math.max(1, open + inProgress + completed);
    const openPct = Math.round((open / total) * 360);
    const inProgPct = Math.round((inProgress / total) * 360);
    const typeTotal = Math.max(1, (this.workOrders.filter(w => w.type === 'preventive').length + this.workOrders.filter(w => w.type !== 'preventive').length));
    const preventiveAngle = Math.round(((this.workOrders.filter(w => w.type === 'preventive').length) / typeTotal) * 360);

    const recent = [...this.workOrders]
      .sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))
      .slice(0, 5);

    const renderLinePath = (series: number[], colorVar: string) => {
      const w = 520; const h = 120; const pad = 10;
      const maxVal = Math.max(1, ...series);
      const stepX = (w - pad * 2) / Math.max(1, series.length - 1);
      const points = series.map((v, i) => {
        const x = pad + i * stepX;
        const y = h - pad - (v / maxVal) * (h - pad * 2);
        return `${x},${y}`;
      }).join(' ');
      return html`<svg width="100%" height="${h}" viewBox="0 0 ${w} ${h}"><polyline fill="none" stroke="var(${colorVar})" stroke-width="2" points="${points}"/></svg>`;
    };

    // Technicians (this month): completed count and avg time
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const completedThisMonth = this.workOrders.filter(w => w.completed_at && w.completed_at >= startOfMonth);
    const validForTech = completedThisMonth.filter(w => !!w.assigned_to && !!this.users[w.assigned_to]);
    const skippedForTech = completedThisMonth.length - validForTech.length;
    const byTech = this.groupBy(validForTech, 'assigned_to');
    const topTech = Object.entries(byTech).map(([techId, arr]) => {
      const avgHrs = this.calculateAverageCompletion(arr as any[]);
      return { tech: this.getUserName(techId), completed: (arr as any[]).length, avgHrs };
    }).sort((a, b) => b.completed - a.completed).slice(0, 5);

    // Assets (this month): WO count and last WO date
    const inMonth = this.workOrders.filter(w => (w.created_at ?? 0) >= startOfMonth);
    const validForAsset = inMonth.filter(w => !!w.asset_id && !!this.assets[w.asset_id]);
    const skippedForAsset = inMonth.length - validForAsset.length;
    const byAsset = this.groupBy(validForAsset, 'asset_id');
    const topAssets = Object.entries(byAsset).map(([assetId, arr]) => {
      const last = Math.max(...(arr as any[]).map(w => w.created_at || 0));
      return { asset: this.assets[assetId] || assetId || 'Asset', count: (arr as any[]).length, last };
    }).sort((a, b) => b.count - a.count).slice(0, 5);

    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <h2 style="margin:0;font-size:1.25rem;">Work Orders Overview</h2>
        </div>

        <!-- Top KPI row -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-header">Total WOs (This Month)</div>
            <div class="kpi-body">
              <div class="kpi-value">${metrics.totalMonth}</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-header">On-Time Completion</div>
            <div class="kpi-body">
              <sl-progress-ring value=${metrics.onTimePct} style="--size:56px; --track-width:8px; --indicator-color: var(--sl-color-success-600);"></sl-progress-ring>
              <div class="kpi-value-small">${metrics.onTimePct}%</div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-header">Avg Completion Time</div>
            <div class="kpi-body">
              <div class="kpi-value">${metrics.avgCompletionHrs}<span class="kpi-suffix"> hrs</span></div>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-header">Reactive vs Planned</div>
            <div class="kpi-body">
              <sl-progress-ring value=${metrics.reactivePct} style="--size:56px; --track-width:8px; --indicator-color: var(--sl-color-primary-600);"></sl-progress-ring>
              <div class="kpi-value-small">${metrics.reactivePct}% Reactive</div>
            </div>
          </div>
        </div>

        <!-- Existing overview cards retained -->
        <div class="overview-cards">
          <div class="overview-card"><h3>Open</h3><p class="value">${open}</p></div>
          <div class="overview-card"><h3>In Progress</h3><p class="value">${inProgress}</p></div>
          <div class="overview-card"><h3>Completed</h3><p class="value">${completed}</p></div>
        </div>

        <!-- Charts row: Type distribution and 30-day trend -->
        <div class="charts-grid">
          <div class="widget">
            <h3>WO Type Distribution</h3>
            <div class="content">
              <div class="donut-chart" style="background: conic-gradient(
                var(--sl-color-primary-600) 0deg ${preventiveAngle}deg,
                var(--sl-color-blue-200) ${preventiveAngle}deg 360deg
              );"></div>
              <div class="chart-legend">
                <div class="legend-item"><span class="legend-color" style="background: var(--sl-color-primary-600);"></span> Preventive (${this.workOrders.filter(w => w.type==='preventive').length})</div>
                <div class="legend-item"><span class="legend-color" style="background: var(--sl-color-blue-200);"></span> Corrective (${this.workOrders.filter(w => w.type!=='preventive').length})</div>
              </div>
            </div>
          </div>
          <div class="widget">
            <h3>WO Status Trend (30 days)</h3>
            <div class="line-chart">
              ${renderLinePath(this.thirtyDayCreatedSeries, '--sl-color-primary-600')}
              ${renderLinePath(this.thirtyDayCompletedSeries, '--sl-color-success-600')}
              <div class="line-legend">
                <span class="dot" style="background: var(--sl-color-primary-600)"></span> Created
                <span class="dot" style="background: var(--sl-color-success-600); margin-left:1rem;"></span> Completed
              </div>
            </div>
          </div>
        </div>

        <!-- Tables -->
        <div class="tables-grid">
          <div class="widget">
            <h3>Top 5 Technicians (This Month)</h3>
            <div style="overflow-x:auto">
              <table class="data-table">
                <thead><tr><th>Technician</th><th>Completed</th><th>Avg Completion Time (hrs)</th></tr></thead>
                <tbody>
                  ${topTech.map(t => html`<tr><td>${t.tech || '-'}</td><td>${t.completed}</td><td>${t.avgHrs}</td></tr>`)}
                </tbody>
              </table>
            </div>
          </div>
          <div class="widget">
            <h3>Top 5 Problem Assets (This Month)</h3>
            <div style="overflow-x:auto">
              <table class="data-table">
                <thead><tr><th>Asset</th><th>Total WOs</th><th>Last WO Date</th></tr></thead>
                <tbody>
                  ${topAssets.map(a => html`<tr><td>${a.asset}</td><td>${a.count}</td><td>${this.formatDate(a.last)}</td></tr>`)}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="widget">
          <h3>Recent Work Orders</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead>
                <tr><th>Description</th><th>Assigned To</th><th>Status</th><th>Created</th><th>Due</th><th>Completed</th></tr>
              </thead>
              <tbody>
                ${recent.map(wo => html`
                  <tr>
                    <td>${wo.description || '-'}</td>
                    <td>${this.getUserName(wo.assigned_to)}</td>
                    <td><sl-badge variant=${this.getStatusVariant(wo.status_cat)}>${this.getStatusLabel(wo.status_cat)}</sl-badge></td>
                    <td>${this.formatDate(wo.created_at)}</td>
                    <td>${this.formatDate(wo.due_date)}</td>
                    <td>${this.formatDate(wo.completed_at)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wo-mini-dashboard': WOMiniDashboard;
  }
}
